import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  styled
} from '@mui/material';
import {
  Info as InfoIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  AccountTree as BranchIcon
} from '@mui/icons-material';
import { appVersion } from '../utils/version';

// Styled components
const VersionChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: '24px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[2],
  },
}));

const VersionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  userSelect: 'none',
}));

interface VersionDisplayProps {
  variant?: 'chip' | 'text' | 'minimal' | 'footer';
  showIcon?: boolean;
  showTooltip?: boolean;
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium';
}

export const VersionDisplay: React.FC<VersionDisplayProps> = ({
  variant = 'chip',
  showIcon = true,
  showTooltip = true,
  color = 'primary',
  size = 'small'
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  
  const versionInfo = appVersion.getFullVersionInfo();
  const displayVersion = appVersion.getShortVersion();
  
  const handleVersionClick = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const versionContent = React.useMemo(() => {
    switch (variant) {
      case 'text':
        return (
          <VersionBox>
            {showIcon && <InfoIcon fontSize="small" />}
            <Typography variant="caption" color="textSecondary">
              {displayVersion}
            </Typography>
          </VersionBox>
        );
      
      case 'minimal':
        return (
          <Typography variant="caption" color="textSecondary">
            {displayVersion}
          </Typography>
        );
      
      case 'footer':
        return (
          <VersionBox>
            <Typography variant="caption" color="textSecondary">
              GaadiKharcha {displayVersion} • Built on {appVersion.getBuildDate()}
            </Typography>
          </VersionBox>
        );
      
      case 'chip':
      default:
        return (
          <VersionChip
            icon={showIcon ? <BuildIcon fontSize="small" /> : undefined}
            label={displayVersion}
            size={size}
            color={color}
            variant="outlined"
            onClick={handleVersionClick}
          />
        );
    }
  }, [variant, showIcon, displayVersion, color, size]);

  const tooltipTitle = showTooltip ? (
    <Box>
      <Typography variant="body2">
        <strong>Version:</strong> {versionInfo.version}
      </Typography>
      <Typography variant="body2">
        <strong>Built:</strong> {appVersion.getBuildDate()} at {appVersion.getBuildTime()}
      </Typography>
      <Typography variant="body2">
        <strong>Environment:</strong> {versionInfo.environment}
      </Typography>
      {variant === 'chip' && (
        <Typography variant="caption" style={{ fontStyle: 'italic' }}>
          Click for more details
        </Typography>
      )}
    </Box>
  ) : '';

  return (
    <>
      {showTooltip && variant !== 'footer' ? (
        <Tooltip title={tooltipTitle} arrow placement="top">
          <Box component="span">
            {versionContent}
          </Box>
        </Tooltip>
      ) : (
        versionContent
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            Application Version Information
          </Box>
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell><strong>Version</strong></TableCell>
                <TableCell>{versionInfo.version}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Build Date</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ScheduleIcon fontSize="small" color="action" />
                    {appVersion.getBuildDate()}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Build Time</strong></TableCell>
                <TableCell>{appVersion.getBuildTime()}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Build Number</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BuildIcon fontSize="small" color="action" />
                    {versionInfo.buildNumber}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Environment</strong></TableCell>
                <TableCell>
                  <Chip 
                    label={versionInfo.environment} 
                    size="small" 
                    color={versionInfo.environment === 'production' ? 'success' : 'warning'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Git Branch</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BranchIcon fontSize="small" color="action" />
                    {versionInfo.gitBranch}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Timestamp</strong></TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {new Date(versionInfo.timestamp).toLocaleString('en-IN')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};



export const SidebarVersion: React.FC = () => (
  <VersionDisplay variant="minimal" size="small" showTooltip={false} />
);

export default VersionDisplay;
